// Recently viewed (sections/recently-viewed.liquid). The list is "de-recently-viewed" in localStorage: product
// handles, newest first (assets/product.js adds the page's product). When the section comes within about 600px of
// the screen, the first `limit` handles other than the current product are fetched in parallel as mini cards
// (/products/<handle>?section_id=product-card-mini), each cached in sessionStorage for the visit. A handle that
// no longer exists (deleted or unpublished: a 404, or the section rendered without a product, data-card-missing) is
// dropped from the list. The section stays hidden when nothing is left.
const KEY = 'de-recently-viewed';
const CACHE = 'de-mini:';

const readList = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && /^[\w-]+$/.test(h)) : [];
  } catch {
    return [];
  }
};

const writeList = (list) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Storage blocked: the list isn't pruned this time.
  }
};

const cached = (handle) => {
  try {
    return sessionStorage.getItem(CACHE + handle);
  } catch {
    return null;
  }
};

const cache = (handle, html) => {
  try {
    sessionStorage.setItem(CACHE + handle, html);
  } catch {
    // Storage full or blocked.
  }
};

// { html } for a card, { gone: true } for a product that no longer exists, null for any other failure.
const fetchCard = async (base, handle) => {
  const hit = cached(handle);
  if (hit) return { html: hit };
  try {
    const response = await fetch(`${base}${encodeURIComponent(handle)}?section_id=product-card-mini`, {
      headers: { Accept: 'text/html' },
    });
    if (response.status === 404) return { gone: true };
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (doc.querySelector('[data-card-missing]')) return { gone: true };
    const card = doc.querySelector('[data-mini]');
    if (!card) return null;
    cache(handle, card.outerHTML);
    return { html: card.outerHTML };
  } catch {
    return null;
  }
};

class RecentlyViewed {
  constructor(el) {
    this.el = el;
    // The section itself is hidden (no box), so its wrapper is watched.
    const target = el.parentElement || el;
    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(target);
  }

  async load() {
    if (this.loaded) return;
    this.loaded = true;
    const { el } = this;
    const limit = Math.max(1, Number(el.dataset.limit) || 6);
    const handles = readList()
      .filter((h) => h !== el.dataset.exclude)
      .slice(0, limit);
    if (!handles.length) return;

    const results = await Promise.all(handles.map((handle) => fetchCard(el.dataset.url || '/products/', handle)));
    const gone = handles.filter((_, i) => results[i]?.gone);
    if (gone.length) writeList(readList().filter((h) => !gone.includes(h)));

    const list = el.querySelector('[data-recent-list]');
    const html = results.filter((result) => result?.html).map((result) => result.html);
    if (!list || !html.length) return;
    list.innerHTML = html.join('');
    el.hidden = false;
  }

  destroy() {
    this.observer?.disconnect();
  }
}

const instances = new WeakMap();
const init = (scope = document) => {
  scope.querySelectorAll('[data-recently-viewed]').forEach((el) => {
    if (!instances.has(el)) instances.set(el, new RecentlyViewed(el));
  });
};

document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-recently-viewed]').forEach((el) => {
    instances.get(el)?.destroy();
    instances.delete(el);
  });
});

init();
